import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink } from 'lucide-react';
import { interspireApi } from '../services/api';
import { UnifiedDomainResult } from '../types';

interface Segment {
  segmentid: number;
  segmentname: string;
}

interface HealthBreakdown {
  segmentName: string;
  listId: number;
  fieldId: number;
  breakdown: { countyState: string; count: number }[];
  total: number;
}

/** Opens Domain Lookup contact report in a new tab when count > 0. */
function CountLink({
  count,
  bucket,
  domain,
  segmentId,
  className = '',
}: {
  count: number;
  bucket: string;
  domain?: string;
  segmentId?: number;
  className?: string;
}) {
  const formatted = count.toLocaleString();
  if (count <= 0) {
    return <span className={className}>{formatted}</span>;
  }
  const params = new URLSearchParams({ bucket });
  if (domain) params.set('domain', domain.replace(/^@/, '').toLowerCase());
  if (segmentId != null) params.set('segmentId', String(segmentId));
  const href = `/domain-lookup/report?${params.toString()}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="Open contact report in new tab"
      className={`inline-flex items-center gap-1 hover:underline ${className}`}
    >
      {formatted}
      <ExternalLink className="h-3.5 w-3.5 opacity-70" />
    </a>
  );
}

export default function DomainLookup() {
  const [searchParams] = useSearchParams();

  // Phase 1: select segment
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [segmentsLoaded, setSegmentsLoaded] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);

  const [segmentFilter, setSegmentFilter] = useState('');

  // Phase 2: main view
  const [health, setHealth] = useState<HealthBreakdown | null>(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  const [domain, setDomain] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<UnifiedDomainResult | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);
  const [showSamples, setShowSamples] = useState(false);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [showMissingLists, setShowMissingLists] = useState(false);

  const loadSegments = useCallback(async () => {
    if (segmentsLoaded) return;
    setLoadingSegments(true);
    try {
      const res = await axios.get<Segment[]>('/api/interspire/segments');
      setSegments(res.data);
      setSegmentsLoaded(true);
      return res.data;
    } catch {
      // ignore
    } finally {
      setLoadingSegments(false);
    }
    return [];
  }, [segmentsLoaded]);

  // Auto-select segment from URL ?segmentId=
  useEffect(() => {
    const paramId = searchParams.get('segmentId');
    if (!paramId) return;
    const id = parseInt(paramId, 10);
    if (isNaN(id)) return;
    loadSegments().then((segs) => {
      if (!segs) return;
      const match = segs.find(s => s.segmentid === id);
      if (match) handleSegmentSelect(match);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSegmentSelect = async (seg: Segment) => {
    setSelectedSegment(seg);
    setHealth(null);
    setHealthError(null);
    setResult(null);
    setResultError(null);
    setDomain('');
    setShowSamples(false);
    setLoadingHealth(true);
    try {
      const res = await axios.get<HealthBreakdown>(`/api/interspire/segments/${seg.segmentid}/health`);
      setHealth(res.data);
    } catch {
      setHealthError('Failed to load health breakdown for this segment.');
    } finally {
      setLoadingHealth(false);
    }
  };

  const handleBack = () => {
    setSelectedSegment(null);
    setHealth(null);
    setHealthError(null);
    setResult(null);
    setResultError(null);
    setDomain('');
    setShowSamples(false);
    setShowExcluded(false);
    setShowMissingLists(false);
    setSegmentFilter('');
  };

  const handleAnalyze = async () => {
    if (!selectedSegment || !domain.trim()) return;
    setAnalyzing(true);
    setResult(null);
    setResultError(null);
    setShowSamples(false);
    setShowExcluded(false);
    setShowMissingLists(false);
    try {
      const res = await interspireApi.investigateDomainForSegment(selectedSegment.segmentid, domain.trim());
      setResult(res.data);
    } catch (e: any) {
      setResultError(e?.response?.data?.error ?? 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleShowSamples = async () => {
    if (!selectedSegment || !domain.trim()) return;
    setLoadingSamples(true);
    try {
      const res = await interspireApi.investigateDomainForSegment(selectedSegment.segmentid, domain.trim(), true);
      setResult(res.data);
      setShowSamples(true);
    } catch {
      // keep existing result
    } finally {
      setLoadingSamples(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Domain Lookup</h1>
        <p className="text-sm text-gray-500 mt-1">
          Select a segment to see its contact breakdown, then analyze a domain against it.
        </p>
      </div>

      {!selectedSegment ? (
        /* ── Phase 1: Segment selector ── */
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6 max-w-xl">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Select a Segment</h2>
          <button
            onClick={loadSegments}
            disabled={segmentsLoaded || loadingSegments}
            className="mb-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loadingSegments ? 'Loading…' : segmentsLoaded ? 'Segments Loaded' : 'Load Segments'}
          </button>

          {segmentsLoaded && segments.length === 0 && (
            <p className="text-sm text-gray-400">No segments found.</p>
          )}

          {segments.length > 0 && (
            <>
              <div className="relative mb-3">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  value={segmentFilter}
                  onChange={e => setSegmentFilter(e.target.value)}
                  placeholder="Filter segments…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                {segmentFilter && (
                  <button
                    onClick={() => setSegmentFilter('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {(() => {
                const filtered = segments.filter(s =>
                  s.segmentname.toLowerCase().includes(segmentFilter.toLowerCase())
                );
                return filtered.length === 0 ? (
                  <p className="text-sm text-gray-400 px-1">No segments match "{segmentFilter}"</p>
                ) : (
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    {filtered.map(seg => (
                      <li key={seg.segmentid}>
                        <button
                          onClick={() => handleSegmentSelect(seg)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors text-sm text-gray-800 flex justify-between items-center"
                        >
                          <span className="font-medium">{seg.segmentname}</span>
                          <span className="text-xs text-gray-400">ID {seg.segmentid}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </>
          )}
        </div>
      ) : (
        /* ── Phase 2: Two-column analysis view ── */
        <div>
          {/* Header bar */}
          <div className="flex items-center gap-3 mb-5">
            <button
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Segments
            </button>
            <span className="text-gray-300">|</span>
            <h2 className="text-base font-semibold text-gray-800">{selectedSegment.segmentname}</h2>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              ID {selectedSegment.segmentid}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ── Left column: Health Breakdown ── */}
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  County / State Breakdown
                </h3>
              </div>

              {loadingHealth && (
                <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
                  Loading breakdown…
                </div>
              )}

              {healthError && (
                <div className="p-5 text-sm text-red-600">{healthError}</div>
              )}

              {health && !loadingHealth && (
                <>
                  {/* Total (top) — opens segment member report (same membership as segment export) */}
                  <div className="px-5 py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</span>
                    <CountLink
                      count={health.total}
                      bucket="segment_members"
                      segmentId={selectedSegment.segmentid}
                      className="text-sm font-bold text-gray-900"
                    />
                  </div>

                  {/* Breakdown rows */}
                  <div className="overflow-y-auto max-h-[480px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr>
                          <th className="text-left px-5 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            County, State
                          </th>
                          <th className="text-right px-5 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Contacts
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {health.breakdown.map(row => (
                          <tr key={row.countyState} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-1.5 text-gray-700">{row.countyState}</td>
                            <td className="px-5 py-1.5 text-right font-medium text-gray-900">
                              {row.count.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Total (bottom) */}
                  <div className="px-5 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</span>
                    <CountLink
                      count={health.total}
                      bucket="segment_members"
                      segmentId={selectedSegment.segmentid}
                      className="text-sm font-bold text-gray-900"
                    />
                  </div>
                </>
              )}
            </div>

            {/* ── Right column: Domain Investigation ── */}
            <div className="flex flex-col gap-4">
              {/* Input card */}
              <div className="bg-white rounded-xl shadow border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Investigate a Domain
                </h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={domain}
                    onChange={e => setDomain(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
                    placeholder="e.g. denvergov.org"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAnalyze}
                    disabled={analyzing || !domain.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {analyzing ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Analyzing…
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                        </svg>
                        Analyze Domain
                      </>
                    )}
                  </button>
                </div>
                {resultError && (
                  <p className="mt-2 text-sm text-red-600">{resultError}</p>
                )}
              </div>

              {/* Results card */}
              {result && (
                <div className="bg-white rounded-xl shadow border border-gray-100 p-5 flex flex-col gap-5">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Results for @{domain.replace(/^@/, '')}
                  </h3>

                  {/* ── Section 1: Whole-database counts ── */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Entire Database</p>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          <tr>
                            <td className="px-4 py-2 text-gray-700">Total contacts</td>
                            <td className="px-4 py-2 text-right font-semibold text-gray-900">
                              <CountLink count={result.db.total} bucket="db_total" domain={domain} />
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 text-amber-700">Unsubscribed</td>
                            <td className="px-4 py-2 text-right font-semibold text-amber-700">
                              <CountLink count={result.db.unsubscribed} bucket="db_unsubscribed" domain={domain} />
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 text-red-700">Bounced</td>
                            <td className="px-4 py-2 text-right font-semibold text-red-700">
                              <CountLink count={result.db.bounced} bucket="db_bounced" domain={domain} />
                            </td>
                          </tr>
                          <tr className="bg-green-50">
                            <td className="px-4 py-2 text-green-800 font-medium">Active</td>
                            <td className="px-4 py-2 text-right font-semibold text-green-800">
                              <CountLink count={result.db.active} bucket="db_active" domain={domain} />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── Section 2: Segment-scoped counts ── */}
                  {result.segment && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Segment — {result.segment.segmentName}
                      </p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <tbody className="divide-y divide-gray-100">
                            <tr>
                              <td className="px-4 py-2 text-gray-700">In segment's lists</td>
                              <td className="px-4 py-2 text-right font-semibold text-gray-900">
                                <CountLink
                                  count={result.segment.listTotal}
                                  bucket="list_total"
                                  domain={domain}
                                  segmentId={selectedSegment.segmentid}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-amber-700">Unsubscribed</td>
                              <td className="px-4 py-2 text-right font-semibold text-amber-700">
                                <CountLink
                                  count={result.segment.listUnsubscribed}
                                  bucket="list_unsubscribed"
                                  domain={domain}
                                  segmentId={selectedSegment.segmentid}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-red-700">Bounced</td>
                              <td className="px-4 py-2 text-right font-semibold text-red-700">
                                <CountLink
                                  count={result.segment.listBounced}
                                  bucket="list_bounced"
                                  domain={domain}
                                  segmentId={selectedSegment.segmentid}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-green-700">Active in lists</td>
                              <td className="px-4 py-2 text-right font-semibold text-green-700">
                                <CountLink
                                  count={result.segment.listActive}
                                  bucket="list_active"
                                  domain={domain}
                                  segmentId={selectedSegment.segmentid}
                                />
                              </td>
                            </tr>
                            <tr className="bg-green-50">
                              <td className="px-4 py-2 text-green-800 font-medium">In segment (matches rules)</td>
                              <td className="px-4 py-2 text-right font-semibold text-green-800">
                                <CountLink
                                  count={result.segment.inSegment}
                                  bucket="in_segment"
                                  domain={domain}
                                  segmentId={selectedSegment.segmentid}
                                />
                              </td>
                            </tr>
                            <tr className="bg-yellow-50">
                              <td className="px-4 py-2 text-yellow-800 font-medium">Eligible but not in segment</td>
                              <td className="px-4 py-2 text-right font-semibold text-yellow-800">
                                <CountLink
                                  count={result.segment.notInSegment}
                                  bucket="not_in_segment"
                                  domain={domain}
                                  segmentId={selectedSegment.segmentid}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── Group A: Excluded by rules ── */}
                  {result.excludedByRules && result.excludedByRules.length > 0 && (
                    <div className="border border-orange-200 dark:border-orange-700/50 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setShowExcluded(v => !v)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/25 dark:hover:bg-orange-900/40 transition-colors text-left"
                      >
                        <span className="text-sm font-semibold text-orange-800 dark:text-orange-300">
                          {result.excludedByRules.length} active contact{result.excludedByRules.length !== 1 ? 's' : ''} excluded by segment rules — click to see why
                        </span>
                        <svg className={`w-4 h-4 text-orange-600 dark:text-orange-400 transition-transform ${showExcluded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showExcluded && (
                        <div className="p-4 space-y-3">
                          {result.ruleFields && result.ruleFields.length > 0 && (
                            <div className="bg-orange-50 dark:bg-orange-900/25 border border-orange-200 dark:border-orange-700/40 rounded px-3 py-2 text-xs text-orange-800 dark:text-orange-300">
                              <span className="font-semibold">Segment requires: </span>
                              {result.ruleFields.map(rf => (
                                <span key={rf.fieldId} className="mr-3">
                                  <span className="font-medium">{rf.fieldName}:</span> {rf.allowedValues.join(', ')}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Email</th>
                                  {result.ruleFields?.map(rf => (
                                    <th key={rf.fieldId} className="px-3 py-2 text-left font-semibold text-gray-500">{rf.fieldName}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {result.excludedByRules.map((c, i) => (
                                  <tr key={i} className="hover:bg-gray-50">
                                    <td className="px-3 py-1.5 text-gray-700 font-mono">{c.email}</td>
                                    {c.fieldValues.map(fv => (
                                      <td key={fv.fieldId} className="px-3 py-1.5">
                                        {fv.value
                                          ? <span className="text-red-700 font-medium">{fv.value}</span>
                                          : <span className="text-gray-300 italic">no value</span>
                                        }
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Group B: In other lists ── */}
                  {result.missingByList && result.missingByList.length > 0 && (
                    <div className="border border-blue-200 dark:border-blue-700/50 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setShowMissingLists(v => !v)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/25 dark:hover:bg-blue-900/40 transition-colors text-left"
                      >
                        <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                          {result.missingByList.reduce((s, r) => s + r.count, 0).toLocaleString()} contacts found in other lists (not this segment's lists) — click to see which lists
                        </span>
                        <svg className={`w-4 h-4 text-blue-600 dark:text-blue-400 transition-transform ${showMissingLists ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showMissingLists && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-4 py-2 text-left font-semibold text-gray-500">List Name</th>
                                <th className="px-4 py-2 text-right font-semibold text-gray-500">Total</th>
                                <th className="px-4 py-2 text-right font-semibold text-gray-500">Active</th>
                                <th className="px-4 py-2 text-right font-semibold text-gray-500">Unsub</th>
                                <th className="px-4 py-2 text-right font-semibold text-gray-500">Bounced</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {result.missingByList.map(r => (
                                <tr key={r.listId} className="hover:bg-gray-50">
                                  <td className="px-4 py-1.5 text-gray-700">{r.listName}</td>
                                  <td className="px-4 py-1.5 text-right font-semibold text-gray-900">{r.count.toLocaleString()}</td>
                                  <td className="px-4 py-1.5 text-right text-green-700">{r.active.toLocaleString()}</td>
                                  <td className="px-4 py-1.5 text-right text-amber-700">{r.unsubscribed.toLocaleString()}</td>
                                  <td className="px-4 py-1.5 text-right text-red-700">{r.bounced.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Explanation */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Explanation
                    </p>
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {result.explanation}
                    </pre>
                  </div>

                  {/* Sample contacts */}
                  {!showSamples && (
                    <button
                      onClick={handleShowSamples}
                      disabled={loadingSamples}
                      className="self-start flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {loadingSamples ? 'Loading…' : 'Show Sample Contacts (up to 25)'}
                    </button>
                  )}

                  {showSamples && result.sampleContacts && result.sampleContacts.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Sample Contacts ({result.sampleContacts.length})
                      </p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-3 py-2 font-semibold text-gray-500">Email</th>
                              <th className="text-center px-3 py-2 font-semibold text-gray-500">Unsub</th>
                              <th className="text-center px-3 py-2 font-semibold text-gray-500">Bounced</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {result.sampleContacts.map((c, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-3 py-1.5 text-gray-700">{c.emailaddress}</td>
                                <td className="px-3 py-1.5 text-center">
                                  {c.unsubscribed > 0 ? (
                                    <span className="text-orange-500 font-semibold">Yes</span>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-center">
                                  {c.bounced > 0 ? (
                                    <span className="text-red-500 font-semibold">Yes</span>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {showSamples && result.sampleContacts && result.sampleContacts.length === 0 && (
                    <p className="text-sm text-gray-400">No sample contacts found for this domain.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
